/* The six digits, generated on the device.
 *
 * This has to agree with `damga_totp` in 006_code_secrets.sql exactly. If the
 * phone and the database disagree by one digit, nobody can ever be stamped, so
 * the JS is checked against vectors generated by Postgres itself rather than
 * against a second opinion written by the same author. See totp.test.ts.
 *
 * WHY NOT WebCrypto
 * `crypto.subtle` exists only in a secure context. The counter and the app are
 * tested on real hardware over a LAN, which is plain http, and the whole point
 * of this code is that it works when things are degraded. A code generator
 * that stops working on the office wifi is not a code generator. @noble/hashes
 * is audited, dependency-free, and runs anywhere.
 *
 * SHA-1 is not a choice made here. It is what RFC 6238 specifies for TOTP and
 * what the database computes, and TOTP's security does not rest on collision
 * resistance. `person_code_secrets.algorithm` has a CHECK constraint pinning
 * it, so this cannot silently diverge either.
 */
import { hmac } from "@noble/hashes/hmac";
import { sha1 } from "@noble/hashes/sha1";

export type CodeSecret = {
  secret: string;
  digits: number;
  period_seconds: number;
  algorithm?: string;
};

/** base64url, unpadded, exactly as `b64url_decode` reads it. */
export function decodeSecret(secretB64Url: string): Uint8Array {
  const padded =
    secretB64Url.replace(/-/g, "+").replace(/_/g, "/") +
    "=".repeat((4 - (secretB64Url.length % 4)) % 4);
  const binary = atob(padded);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

/** `damga_code_window`: floor(epoch seconds / period). */
export function codeWindow(atMs: number, periodSeconds: number): number {
  return Math.floor(atMs / 1000 / periodSeconds);
}

/**
 * RFC 4226 dynamic truncation over an 8-byte big-endian counter.
 *
 * The counter is written with a DataView rather than by shifting, because
 * JavaScript bitwise operators coerce to 32 bits and a TOTP counter passes
 * 2^32 in the year 2106 for 60-second windows. Getting that wrong produces
 * correct codes today and wrong ones later, which is the worst kind of bug.
 */
export function totp(secretB64Url: string, counter: number, digits = 6): string {
  const message = new Uint8Array(8);
  new DataView(message.buffer).setBigUint64(0, BigInt(counter), false);

  const mac = hmac(sha1, decodeSecret(secretB64Url), message);
  const offset = mac[19] & 0x0f;
  const truncated =
    ((mac[offset] & 0x7f) << 24) |
    (mac[offset + 1] << 16) |
    (mac[offset + 2] << 8) |
    mac[offset + 3];

  return String(truncated % 10 ** digits).padStart(digits, "0");
}

export type LiveCode = {
  code: string;
  /** Whole seconds until this code is replaced. Never 0, so the UI never
   *  renders "New code in 0s" for a whole second before it changes. */
  secondsLeft: number;
  window: number;
};

/**
 * The code to show right now, and how long it lasts.
 *
 * Takes the instant rather than reading the clock, so the caller owns the
 * cadence and this stays a pure function that tests can pin.
 */
export function currentCode(secret: CodeSecret, atMs: number): LiveCode {
  const period = secret.period_seconds;
  const window = codeWindow(atMs, period);
  const elapsed = (atMs / 1000) % period;
  return {
    code: totp(secret.secret, window, secret.digits),
    secondsLeft: Math.max(1, Math.ceil(period - elapsed)),
    window,
  };
}
